import EventBus from '@/eventBus'
import Socket from '@/socket'
import * as Sentry from '@sentry/vue'
import PresentationApi from '@/api/presentation'
import SlideApi from '@/api/slide'
import utils from '@/util'
import mixpanelUtils from '@/utils/mixpanel.utils'
import EventCategories from '@/constant/eventCategories'
import QuizStatuses from '@/constant/QuizStatuses'
import { createDebug } from '@/utils/debug'

// Toggle in the browser console with: localStorage.debug = 'aha:ppt:auto-start'
const log = createDebug('ppt:auto-start')

// AHA-43274 gates three separate things — the auto-stop itself, the arriving quiz's lobby stamp, and
// the stood-down frame's send guard — so the three have to switch together or the flag is not really
// one flag.
const AUTO_STOP_QUIZ_FLAG = 'featureToggles/canUsePptAutoStopQuiz'
// Covers pickAnswer and scored embed-app slides. The three quiz paths here — arrival stamp,
// auto-stop, stand-down reset — must all agree on what counts as a quiz, so they share one getter.
const IS_QUIZ_SLIDE_GETTER = 'slideModule/isQuizSlide'

const actions = {
  handleActiveSlidePptAddinWhenOpenLoadSlide({ rootState, dispatch }) {
    const isPresenting = utils.getParamValue('presenting')
    if (!rootState.isEnableAddinPowerpoint) return
    if (!isPresenting) return
    if (document.hidden) return
    dispatch('startActiveSlidePPT', 'Open load slide')
  },
  autoStartActiveSlideFromView({
    rootState, rootGetters, commit, dispatch,
  }, source = 'Auto start') {
    if (!rootState.isEnableAddinPowerpoint) return
    if (!rootGetters['featureToggles/canUseAddinPowerpointAutoStart']) return
    if (!rootState.presentation.id || !rootState.slideActive.id) return
    const alreadyPresentingThisSlide = rootState.presentation.presenting
      && rootState.presentation.activeSlide === rootState.slideActive.id
    // When this slide is already the presenting slide we normally bail. But if it
    // had lost focus and just regained it (pptSlideLostFocus), re-assert it anyway
    // so refocusing a slide always restarts it. Consume the flag on re-assert.
    if (alreadyPresentingThisSlide && !rootState.pptSlideLostFocus) {
      return
    }
    const wasLostFocus = rootState.pptSlideLostFocus
    if (wasLostFocus) {
      commit('setPptSlideLostFocus', false, { root: true })
    }
    log('autoStart trigger', rootState.slideActive.id, '| wasLostFocus =', wasLostFocus)
    dispatch('startActiveSlidePPT', source)
  },
  // AHA-43274: the departure-side mirror of the arriving quiz's lobby stamp in startActiveSlidePPT,
  // for the frame that has just been stood down (pptSlideLostFocus -> true).
  //
  // LOCAL ONLY. No API call, no /activeSlide publish, no mixpanel event. A stood-down frame is by
  // definition NOT the frame holding the audience — some other frame's slide is on screen — so any
  // write it makes here would be a wrong-slide write over the frame that legitimately owns the show,
  // which is the AHA-43274 failure itself. The audience is moved by the ARRIVING frame's
  // startActiveSlidePPT, and that call already stamps LOBBY_SCREEN for the slide it publishes.
  //
  // What this fixes is the departing frame's own view of itself. Its slideActive object persists for
  // the whole show (it is one slide per frame, and it does not change when PowerPoint moves), so
  // whatever quizStatus the last run left on it — QUESTION_SCREEN, RESULT_SCREEN — stays there while
  // the frame sits in the background, and the lobby-keyed getters that read slideActive.quizStatus
  // directly (music, chat, autoplay, hotkeys) keep behaving as though the question were still up.
  // Navigating back re-enters through startActiveSlidePPT, which normalises it — but only under the
  // flag, and only at the moment of arrival; until then the frame is stale.
  //
  // Guards match the arriving stamp exactly (same flag, same isQuizSlide test) so flag-off leaves
  // behaviour identical and a non-quiz frame is untouched.
  resetLocalQuizToLobbyOnStandDown: ({ rootState, rootGetters, dispatch }) => {
    if (!rootState.isEnableAddinPowerpoint) return
    if (!rootGetters[AUTO_STOP_QUIZ_FLAG]) return
    if (!rootGetters[IS_QUIZ_SLIDE_GETTER](rootState.slideActive)) return
    // continueQuiz is the other half of "this quiz is over", and stamping the lobby without it only
    // half-lands. It is a run-scoped latch, not a per-slide field: startQuizThisScreen sets it, and
    // the only things that clear it are backward navigation (checkSlideMovingDirection),
    // leaving presenting, and a results reset — none of which a background add-in frame goes
    // through. QuizSlideContent.mounted reads it to choose lobby vs auto-start, so a frame stood
    // down mid-question keeps a latched true and any later remount of its quiz skips the lobby we
    // just stamped and runs straight into the countdown. That is the state this reset exists to
    // clear, arriving one mount later.
    //
    // Cleared before the quizStatus check below rather than after it, because the two are answers to
    // different questions — quizStatus is what is on screen now, continueQuiz is what the next mount
    // does — and "the screen already reads lobby" is not a reason to leave the auto-start armed.
    //
    // Assigned directly because the field has no mutation; every other clear site writes it the same
    // way (checkSlideMovingDirection, Middle._autoPrevSlide, ResetSlideResultsSetting) and the store
    // is not strict.
    const wasContinueQuiz = rootState.continueQuiz
    rootState.continueQuiz = false
    if (rootState.slideActive.quizStatus === QuizStatuses.LOBBY_SCREEN) return
    log('standDown quiz -> lobby (local only) |', rootState.slideActive.id,
      '| was =', rootState.slideActive.quizStatus, '| continueQuiz was =', wasContinueQuiz)
    dispatch('updateSlideActiveAction', { quizStatus: QuizStatuses.LOBBY_SCREEN }, { root: true })
  },
  // AHA-43274: the auto-STOP counterpart to autoStartActiveSlideFromView. The tag poll
  // (services/office/current-slide) reports through onNoAhaSlideOwner when THIS frame was the one
  // presenting and the show has since settled on a slide no add-in frame claims — i.e. the
  // presenter moved off the quiz onto an ordinary slide — and this ends the quiz by resetting it to
  // its lobby screen.
  //
  // It is the QUIZ reset that does the work, not a `presenting` flip, and that is the whole reason an
  // earlier cut of this "did not seem to do anything". Flipping `presenting` ends the PRESENTATION; it
  // does not reset the QUIZ, whose screen is driven by slideActive.quizStatus (LOBBY / RULE /
  // COUNTDOWN / QUESTION / RESULT). Leave that at QUESTION_SCREEN and the audience keeps staring at
  // the running question with its scoreboard — exactly the complaint AHA-43274 is about — no matter
  // what `presenting` says.
  //
  // So this is NOT the mirror of startActiveSlidePPT, and it is not the mirror of the app's other
  // stop paths either — BackToEditorButton._updateSlideQuizStatus, SlideWrapper's Escape handler and
  // ContextMenu.stopPresenting each reset the quiz AND flip `presenting`, because each is a
  // deliberate "stop presenting". This one is not: the presenter merely navigated onto an ordinary
  // slide. Only the quiz goes back to its lobby; the session is left alone, so `presentation.presenting`
  // stays true here. Two consequences worth knowing before changing this:
  //   - the `!presenting` guard below never becomes false, so a later navigation onto another plain
  //     slide re-runs this reset rather than no-opping. It is an idempotent PUT, so that is cheap.
  //   - the slide payload carries `presenting: false` of its own, matching the other three paths.
  //
  // Guards, in order: the whole feature is a pure no-op unless the add-in is enabled AND the new
  // flag is on (flag off => original behaviour, the quiz keeps running on a plain slide). Then it
  // bails when there is nothing to stop — no presentation, already not presenting, or a frame whose
  // own slide is not a quiz at all — so the poll firing this once per navigation onto a plain slide
  // costs at most one guarded early return.
  //
  // The caller cannot make these checks: current-slide is deliberately a leaf with no store access
  // (importing @/vuex/store there would close an import cycle through the module driving the poll),
  // which is why the quiz test lives here rather than in the poll that decides whose slide is up.
  stopActiveSlidePPT: async ({ rootState, rootGetters, dispatch }, trigger = 'Auto stop') => {
    if (!rootState.isEnableAddinPowerpoint) return
    if (!rootGetters[AUTO_STOP_QUIZ_FLAG]) return
    if (!rootState.presentation.id) return
    // Nothing to end. Checked here rather than by the caller so the "already stopped" case is a
    // silent no-op instead of a redundant presenting:false round trip to the server.
    if (!rootState.presentation.presenting) return
    // Only a QUIZ frame auto-stops. A poll or word cloud left up on a plain slide is harmless — the
    // audience can keep answering and the presenter may well intend to come back to it — whereas a
    // quiz has a running scoreboard the audience is watching, which is the complaint AHA-43274 is
    // about. isQuizSlide covers pickAnswer and scored embed-app slides.
    if (!rootGetters[IS_QUIZ_SLIDE_GETTER](rootState.slideActive)) return
    // Every reference stop path gates both of its calls on this: an admin viewing someone else's
    // presentation must not mutate it. Same guard, same reason.
    if (rootState.isAdmin) return
    try {
      // Reset the quiz to its lobby. Without this the presenting flag goes false and the audience
      // stays on the question — see the note above.
      await SlideApi.updateSlideById(rootState.slideActive.id, {
        quizStatus: QuizStatuses.LOBBY_SCREEN,
        socketId: Socket.getSocketId(),
        accessCode: rootState.presentation.uniqueAccessCode,
        activeSlide: rootState.slideActive.id,
        presentationId: rootState.presentation.id,
        presenting: false,
        trigger: 'stopActiveSlidePPT in ppt-addin.actions.js',
      })
      // Keep this frame's own copy in step with what the server now holds. Without it the presenter
      // navigating back onto the quiz slide re-starts from a slideActive that still claims to be
      // mid-question.
      dispatch('updateSlideActiveAction', { quizStatus: QuizStatuses.LOBBY_SCREEN }, { root: true })
      log('autoStop trigger', rootState.slideActive.id, '| quiz -> lobby')
      // Named for what this actually DID — the lobby reset — and carrying no `presenting` property,
      // because the session is still live. An earlier cut reported `${trigger} > Stop presenting`
      // with presenting:false on every navigation onto a plain slide: a real funnel step and a real
      // property, both describing a stop that never happened, and nothing downstream could tell.
      // If this path ever does flip `presenting`, the name and the payload move together.
      mixpanelUtils.track(
        `PPT Addin > ${trigger} > Reset quiz to lobby`,
        {
          event_category: EventCategories.PRESENTATION_EDITOR,
          socketId: Socket.getSocketId(),
          presentationId: rootState.presentation.id,
          activeSlidePreseting: rootState.slideActive.id,
          activeSlideAccessCode: rootState.presentation.uniqueAccessCode,
          quizStatus: QuizStatuses.LOBBY_SCREEN,
        },
      )
    } catch (error) {
      Sentry.captureException(error)
    }
  },
  startActiveSlidePPT: async ({ rootState, rootGetters, dispatch }, trigger = 'Auto start') => {
    try {
      if (!rootState.presentation.id || !rootState.slideActive.id) return
      // AHA-43274: a frame that has stood down does not send. pptSlideLostFocus is set by the tag
      // poll on either settled not-MINE verdict — the show moved to another add-in frame, or onto an
      // ordinary slide — and cleared the moment this frame resolves MINE again.
      //
      // The hole this closes is App.vue's _triggerReloadToSyncLoginState, which on every
      // visibilitychange calls straight in here with 'Reload sync' and no notion of who owns the
      // show. A background frame reaching this code sends PUT /presentations (presenting: true) and
      // PUT /slides/next naming ITS OWN slide as active — taking the audience off whichever frame is
      // actually presenting — plus two Mixpanel events describing a start that should not have
      // happened. Since the lobby stamp below, it also resets the CURRENT presenter's live quiz.
      //
      // The guard belongs HERE and not in autoStartActiveSlideFromView, and the ordering is the
      // whole reason it works. That action consumes the flag before dispatching to us, so the
      // legitimate re-assert arrives with it already false and passes; Reload sync bypasses that
      // action entirely, so it arrives with the flag still set and stops. A guard placed one level
      // up would block the re-assert and let Reload sync through — precisely backwards.
      //
      // Not gated on isQuizSlide, unlike the auto-stop. Publishing another frame's audience onto
      // this frame's slide is wrong whatever type that slide is; the quiz case is only the one where
      // the damage is visible on a scoreboard. Narrowing it would leave the same bug live for polls
      // and word clouds while looking fixed.
      //
      // isPptFrameStoodDown is the same condition the quiz content and the audio module now switch
      // on, so all three stand down and stand back up together. It adds the active-view term to what
      // this guard used to test on its own, which can only ever suppress LESS: the view machine
      // commits 'read' before it starts the poll that sets the flag, so a slideshow frame always has
      // both — while a frame carrying the latch back out to the EDITOR no longer refuses to start.
      if (rootGetters.isPptFrameStoodDown) {
        log('start suppressed', rootState.slideActive.id, '| trigger =', trigger,
          '| this frame does not hold the show')
        return
      }
      if (!rootState.presentation.presenting) {
        EventBus.$emit('start-presenting-now')

        const payloadPresenting = {
          presenting: true,
          socketId: Socket.getSocketId(),
          activeSlidePreseting: rootState.slideActive.id,
          activeSlideAccessCode: rootState.presentation.uniqueAccessCode,
          presentFromPPT: true,
        }

        await PresentationApi.updatePresentationByPresentationId(
          rootState.presentation.id,
          payloadPresenting,
        )
        mixpanelUtils.track(
          `PPT Addin > ${trigger} > Start presenting`,
          {
            event_category: EventCategories.PRESENTATION_EDITOR,
            ...payloadPresenting,
          },
        )
      }
      const payloadSlide = {
        activeSlide: rootState.slideActive.id,
        socketId: Socket.getSocketId(),
        accessCode: rootState.presentation.uniqueAccessCode,
        timestamp: new Date().getTime(),
        from: 'startActiveSlidePPT',
      }
      // AHA-43274: stamp the arriving quiz back to its lobby, which is what the normal (non-add-in)
      // flow has always done and this path never did.
      //
      // PresentationEditor.handleUpdateNextSlide builds the same payload for the same API and sets
      // quizStatus = LOBBY_SCREEN on it unconditionally, so in the editor a quiz is normalised by
      // WHOEVER ARRIVES. That is why navigating away from a mid-question quiz and back always shows
      // the lobby there: nothing resets it on the way out, the arrival overwrites it on the way in.
      // SlideContent.syncPluginQuizLobbyOnEntry says the same in as many words for plugin quizzes —
      // "force the lobby, regardless of any persisted quizStatus".
      //
      // The add-in never reaches handleUpdateNextSlide (PowerPoint owns navigation, and each frame is
      // its own presenter instance), so it was missing the normalisation entirely and a quiz reshown
      // from a previous run came back mid-question. This is the same one-line stamp on the same call,
      // so no extra request — and unlike the departure-side reset, publishing THIS frame's slide id
      // as active over /activeSlide is correct here, because it genuinely is the active slide now.
      //
      // Not conditioned on the current quizStatus: the local copy can be stale in a way the editor's
      // never is, since this frame's slideActive does not change when the show moves — it is the same
      // slide object across an entire presentation, carrying whatever status the last run left on it.
      if (rootGetters[AUTO_STOP_QUIZ_FLAG]
        && rootGetters[IS_QUIZ_SLIDE_GETTER](rootState.slideActive)) {
        payloadSlide.quizStatus = QuizStatuses.LOBBY_SCREEN
        // Mirror locally as well. handleUpdateNextSlide has no need to — navigating loads a fresh
        // slideActive — but here the object persists, and lobby-keyed getters (music, chat, autoplay)
        // read slideActive.quizStatus directly and would misfire on the stale value.
        dispatch('updateSlideActiveAction', { quizStatus: QuizStatuses.LOBBY_SCREEN }, { root: true })
      }
      if (rootState.slideActive.type !== 'leaderboard') {
        await SlideApi.updateNextSlideById(rootState.presentation.id, payloadSlide)
      }
      // rootState.presentation.activeSlide = rootState.slideActive.id
      // rootState.presentation.presenting = true
      dispatch('updatePresentation', {
        activeSlide: rootState.slideActive.id,
        presenting: true,
      }, { root: true })
      if (rootState.slideActive.type === 'leaderboard') {
        EventBus.$emit('sort-slide', async () => {
          await SlideApi.updateNextSlideById(rootState.presentation.id, payloadSlide)
        })
      }
      mixpanelUtils.track(
        `PPT Addin > ${trigger} > Active slide`,
        {
          event_category: EventCategories.PRESENTATION_EDITOR,
          ...payloadSlide,
        },
      )
    } catch (error) {
      Sentry.captureException(error)
    }
    // EventBus.$emit('makeSlideActive', this.$store.state.slideActive)
  },
}

export default actions
